import { Mail, MapPin, Phone, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResourceBadge } from '@/components/ui/resource-badge';
import { Separator } from '@/components/ui/separator';
import { PaymentDetails } from '@/lib/api/payments';
import { CustomerResourceBadge } from '@/components/customer/CustomerResourceBadge';

interface PaymentCustomerCardProps {
  payment: PaymentDetails;
}

export function PaymentCustomerCard({ payment }: PaymentCustomerCardProps) {
  const { t } = useTranslation('payments');

  // Guard clause for missing customer data
  if (!payment.customer) {
    return (
      <Card className="lg:sticky lg:top-0">
        <CardHeader>
          <CardTitle>{t('customerInformation')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{t('customerCard.noCustomerInfo')}</p>
        </CardContent>
      </Card>
    );
  }

  const customer = payment.customer;

  return (
    <Card className="lg:sticky lg:top-0">
      <CardHeader>
        <CardTitle>{t('customerInformation')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4">
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('customerId')}</p>
            {customer.id ? (
              <CustomerResourceBadge customerId={customer.id} variant="outline" />
            ) : (
              <p className="text-sm">{t('notAvailable')}</p>
            )}
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('name')}</p>
            <div className="flex items-center">
              <User className="text-muted-foreground mr-2 h-4 w-4" />
              <p>
                {[customer.firstName, customer.lastName].filter(Boolean).join(' ') || t('notAvailable')}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('email')}</p>
            <div className="flex items-center">
              <Mail className="text-muted-foreground mr-2 h-4 w-4" />
              <p>{customer.email || t('notAvailable')}</p>
            </div>
          </div>

          {customer.billingAddress?.phone && (
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm font-medium">{t('phone')}</p>
              <div className="flex items-center">
                <Phone className="text-muted-foreground mr-2 h-4 w-4" />
                <p>{customer.billingAddress.phone}</p>
              </div>
            </div>
          )}
        </div>

        <Separator className="my-6" />

        <div className="grid grid-cols-1 gap-6">
          {customer.billingAddress && (customer.billingAddress.address1 || customer.billingAddress.city || customer.billingAddress.country) && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('billingAddress')}
              </h3>
              <div className="text-sm leading-relaxed">
                {(customer.billingAddress.firstName || customer.billingAddress.lastName) && (
                  <p className="font-medium">{[customer.billingAddress.firstName, customer.billingAddress.lastName].filter(Boolean).join(' ')}</p>
                )}
                {customer.billingAddress.address1 && <p>{customer.billingAddress.address1}</p>}
                {(customer.billingAddress.city || customer.billingAddress.state || customer.billingAddress.postal) && (
                  <p>{[customer.billingAddress.city, customer.billingAddress.state, customer.billingAddress.postal].filter(Boolean).join(', ')}</p>
                )}
                {customer.billingAddress.country && <p>{customer.billingAddress.country}</p>}
              </div>
            </div>
          )}

          {customer.shippingAddress && (customer.shippingAddress.address1 || customer.shippingAddress.city || customer.shippingAddress.country) && (
            <div className="space-y-4">
              <h3 className="flex items-center font-semibold">
                <MapPin className="mr-2 h-4 w-4" />
                {t('shippingAddress')}
              </h3>
              <div className="text-sm leading-relaxed">
                {(customer.shippingAddress.firstName || customer.shippingAddress.lastName) && (
                  <p className="font-medium">{[customer.shippingAddress.firstName, customer.shippingAddress.lastName].filter(Boolean).join(' ')}</p>
                )}
                {customer.shippingAddress.address1 && <p>{customer.shippingAddress.address1}</p>}
                {(customer.shippingAddress.city || customer.shippingAddress.state || customer.shippingAddress.postal) && (
                  <p>{[customer.shippingAddress.city, customer.shippingAddress.state, customer.shippingAddress.postal].filter(Boolean).join(', ')}</p>
                )}
                {customer.shippingAddress.country && <p>{customer.shippingAddress.country}</p>}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
